import { useField } from 'formik'
import { useAutofill } from 'components/Wizards/Autofill/AutofillContext'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { FieldProps } from './Types'
import { Field as BaseField } from '../Field/Field'
import Styles from './Field.module.scss'
import { BrandKitField } from 'components/Wizards/MobileWizards/MobileWizard/Fields/BrandKitField'
import { CountField } from 'components/Wizards/MobileWizards/MobileWizard/Fields/CountField'
import { StringInput } from 'components/Wizards/MobileWizards/Form/StringInput'
import { ArrayInput } from 'components/Wizards/MobileWizards/Form/ArrayInput'
import { BooleanInput } from 'components/Wizards/Wizard/Form/BooleanInput'
import { FilePathFormikField } from 'components/Wizards/FilePathFormikField/FilePathFormikField'
import { TopicField } from '../MobileWizard/Fields/TopicField'
import { MediaPickerField } from '../MobileWizard/Fields/MediaPickerField'

const NAMED_FIELDS = ['brand_kit_id', 'project', 'topic', 'initial_media_file_id', 'selected_images']

const NamedField = ({ name, schema, isLoading }: Pick<FieldProps, 'name' | 'schema' | 'isLoading'>) => {
  return (
    <>
      {name === 'brand_kit_id' && <BrandKitField />}
      {name === 'project' && (
        <FilePathFormikField
          isNameLoading={isLoading}
          placeholder={'Name your file...'}
          inputVariant="grey"
          locationPickerVariant="tertiary"
          locationPickerFullWidth
        />
      )}
      {name === 'topic' && <TopicField schema={schema} isLoading={isLoading} />}
      {(name === 'initial_media_file_id' || name === 'selected_images') && (
        <MediaPickerField name={name} schema={schema} />
      )}
    </>
  )
}

const TypedField = ({ name, schema, autoFocus }: FieldProps) => {
  const { type } = schema.describe()
  return (
    <>
      {type === 'string' && <StringInput name={name} schema={schema} autoFocus={autoFocus} />}
      {type === 'array' && <ArrayInput name={name} schema={schema} autoFocus={autoFocus} />}
      {type === 'boolean' && <BooleanInput name={name} />}
      {type === 'number' && <CountField name={name} schema={schema} />}
    </>
  )
}

const Field = ({ name, schema, autoFocus }: FieldProps) => {
  const {
    meta: { label, description, hide, type },
    nullable,
    optional,
  } = schema.describe()
  const isRequired = !nullable && !optional
  const [, meta] = useField(name)
  const { touched, error } = meta

  const { isLoading } = useAutofill()
  const loading = isLoading(name)

  if (hide) return null

  return (
    <BaseField label={label} isRequired={isRequired} description={description}>
      {loading && (
        <>
          {type === 'textarea' && <TextFieldLoadingState lines={4} />}
          {type !== 'textarea' && <TextFieldLoadingState lines={2} />}
        </>
      )}
      {!loading && (
        <>
          {NAMED_FIELDS.includes(name) && <NamedField name={name} schema={schema} isLoading={loading} />}
          {!NAMED_FIELDS.includes(name) && <TypedField name={name} schema={schema} autoFocus={autoFocus} />}
        </>
      )}
      {touched && error && <div className={Styles.error}>{error}</div>}
    </BaseField>
  )
}

export { Field }
